import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FolderOpen, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Project } from '@/types';

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
  onDelete?: (projectId: number) => void;
}

const useCaseColors: Record<string, string> = {
  family_law: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  compliance_dora: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100',
  compliance_iso_27001: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
  compliance_esg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
  compliance_soc2: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  rfp_response: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-100',
  security_questionnaire: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100',
  service_proposal: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100',
};

export function ProjectCard({ project, onClick, onDelete }: ProjectCardProps) {
  const { t } = useTranslation();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(project.id);
    }
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CardHeader>
        <div className="flex items-start justify-between mb-2">
          <FolderOpen className="h-8 w-8 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <Badge className={useCaseColors[project.use_case] || 'bg-gray-100 text-gray-800'}>
              {t('useCases.' + project.use_case)}
            </Badge>
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
        <CardTitle className="line-clamp-2 break-words">{project.name}</CardTitle>
        <CardDescription className="line-clamp-2">
          {project.description || t('common.noDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Last updated */}
          <div className="text-xs text-muted-foreground">
            {t('common.updated')}{' '}
            {formatDistanceToNow(new Date(project.updated_at || project.created_at), {
              addSuffix: true,
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
